import { formatPostCode, HpMethodTypes } from '@avantia/client-and-server-utilities';
import { isNil } from 'lodash';
import { analyticsUserType, GTM_EVENT_TYPES, sendAnalytics } from 'analytics';
import type { AuthType } from 'interfaces/authTypes';
import { Policy, Quote } from 'interfaces/customerDataTypes';
import { ErrorMap } from 'interfaces/errorTypes';
import { State } from 'reducers/initialState';
import { getAgentSearchParams } from 'storage/agentSearch';
import { getConfig } from '../config/configuration';
import type { HpDispatchFunction } from './actionTypes';
import { fetchResolver } from './fetchResolver';

export interface CustomerInsuranceSummaryPayload {
  authToken?: string;
  urlAuthType: AuthType;
}

export interface CustomerInsuranceSummaryResponse {
  policies?: Policy[];
  quotes?: Quote[];
  errors?: ErrorMap;
  authType: AuthType;
}

export interface CustomerInsuranceSummaryErrorPayload {
  error: string;
  authType: AuthType;
}

export interface CustomerInsuranceSummaryRequestBody {
  emailAddress?: string;
  postcode?: string;
  policyDetailsId?: string;
  searchAllPoliciesByEmail?: boolean;
}

export type CustomerInsuranceSummaryActionPayload =
  | CustomerInsuranceSummaryResponse
  | CustomerInsuranceSummaryErrorPayload
  | { urlAuthType: AuthType };

interface RequestProps {
  dispatch: HpDispatchFunction<CustomerInsuranceSummaryActionPayload>;
  url: string;
  authType: AuthType;
  method?: HpMethodTypes;
  data?: CustomerInsuranceSummaryRequestBody;
}

const authenticationRequest = ({
  dispatch,
  url,
  authType,
  method = 'GET',
  data
}: RequestProps): Promise<void> | undefined => {
  return fetchResolver({
    options: { url, method, description: `Get policies and quotes`, data },
    dispatch,
    successAction: (response) => {
      const quotes = (response as CustomerInsuranceSummaryResponse).quotes;
      const policies = (response as CustomerInsuranceSummaryResponse).policies;
      sendAnalytics({
        event: GTM_EVENT_TYPES.LOGGED_IN,
        detail: {
          usertype: analyticsUserType(authType === 'agent'),
          policy_count: policies ? policies.length : 0,
          quote_count: quotes ? quotes.length : 0
        }
      });
      dispatch({ type: 'CUSTOMER_INSURANCE_SUMMARY_SUCCESS', payload: { ...response, authType } });
    },
    failedAction: (err) => {
      dispatch({ type: 'CUSTOMER_INSURANCE_SUMMARY_ERROR', payload: { error: err.message, authType } });
    }
  });
};

export function getCustomerInsuranceSummaryAction(payload: CustomerInsuranceSummaryPayload) {
  return (
    dispatch: HpDispatchFunction<CustomerInsuranceSummaryActionPayload>,
    getState: () => State
  ): Promise<void> | undefined => {
    const { websiteEndpoint } = getConfig();
    const { authToken, urlAuthType } = payload;

    dispatch({ type: 'CUSTOMER_INSURANCE_SUMMARY_LOADING', payload: { urlAuthType } });
    const state = getState();
    const currentAuthType = state.authentication.authType;

    if (urlAuthType === 'agent') {
      const searchParams = getAgentSearchParams();
      if (!searchParams) {
        return authenticationRequest({
          dispatch,
          url: `${websiteEndpoint}/api/customer/summary/session-token`,
          authType: currentAuthType
        });
      }

      const params = new URLSearchParams(searchParams);
      const policyDetailsId = params.get('policyDetailsId');
      const emailAddress = params.get('emailAddress');
      const postcode = params.get('postcode');
      const formattedPostcode = isNil(postcode) ? undefined : formatPostCode(postcode as string);

      const data: CustomerInsuranceSummaryRequestBody = {
        emailAddress: isNil(emailAddress) ? undefined : emailAddress,
        postcode: formattedPostcode,
        policyDetailsId: isNil(policyDetailsId) ? undefined : policyDetailsId
      };

      return authenticationRequest({
        dispatch,
        url: `${websiteEndpoint}/api/customer/summary/agent-authentication`,
        authType: currentAuthType,
        method: 'POST',
        data
      });
    }

    if (!authToken) {
      return authenticationRequest({
        dispatch,
        url: `${websiteEndpoint}/api/customer/summary/session-token`,
        authType: currentAuthType
      });
    }

    return authenticationRequest({
      dispatch,
      url: `${websiteEndpoint}/api/customer/summary/${urlAuthType}/url-token/${encodeURIComponent(authToken)}`,
      authType: currentAuthType
    });
  };
}
